/**
 * reachMixRequiredClientEnv
 * -------------------------
 * Why this module exists (Operator → Reviewer 9, 2026-03-24):
 * `src/services/firebase/index.ts` calls `initializeApp` at **import** time. When Cloudflare Pages
 * ships a build **without** `VITE_FIREBASE_*`, the SDK can mis-initialize and downstream providers
 * throw — users see a **blank** `#root` even though HTTP 200 returned a Vite shell.
 *
 * We **must not** import the Firebase module until we know the minimum public env is present.
 * This file only reads `import.meta.env` (no Firebase side effects).
 */
export const REACHMIX_MIN_FIREBASE_VITE_KEYS = [
  'VITE_FIREBASE_API_KEY',
  'VITE_FIREBASE_AUTH_DOMAIN',
  'VITE_FIREBASE_PROJECT_ID',
] as const;

export type ReachMixMinFirebaseViteKey = (typeof REACHMIX_MIN_FIREBASE_VITE_KEYS)[number];

/**
 * Returns env keys that are missing or still obvious placeholders.
 * Coordinators: real values must be set in **Cloudflare Pages → Settings → Environment variables**
 * for Production (and Preview if you test previews).
 */
export function listMissingReachMixFirebaseClientEnvKeys(): ReachMixMinFirebaseViteKey[] {
  const env = import.meta.env;
  const missing: ReachMixMinFirebaseViteKey[] = [];

  for (const key of REACHMIX_MIN_FIREBASE_VITE_KEYS) {
    const raw = env[key as keyof ImportMetaEnv];
    const val = raw == null ? '' : String(raw).trim();
    if (!val) {
      missing.push(key);
      continue;
    }
    // Treat shipped .env.example placeholders as "unset" so staging does not silently boot broken.
    if (/your_firebase|placeholder|changeme|xxx/i.test(val)) {
      missing.push(key);
      continue;
    }
    /**
     * Firebase **Web** API keys almost always start with `AIza` and are ~39 characters total.
     * Wrong secret types or typos often pass the "non-empty" check but yield `auth/invalid-api-key`
     * at runtime (blank UI after hydration — Operator 2026-03-24).
     */
    if (key === 'VITE_FIREBASE_API_KEY') {
      const looksLikeFirebaseWebKey = /^AIza[0-9A-Za-z\-_]{30,}$/.test(val);
      if (!looksLikeFirebaseWebKey) {
        missing.push(key);
      }
    }
  }

  return missing;
}
